// ============================================================================
// SubOnTheLineField — who cleans THIS line, recorded on the line itself.
//
// ⛔ THERE IS ONE PLACE TO SAY WHO CLEANS A BUILDING AND WHAT THEY ARE PAID,
// and it is the service line. Ben, 2026-09-15. This control replaces a
// read-only "Provider" label that sat beside a button sending the account
// manager to a separate form. Two consequences of that arrangement:
//
//   1. There was NO WAY to record a FIRST sub on the building card at all.
//   2. The separate form's no-outgoing-sub path went looking for a line whose
//      job name was literally "Primary Crew", found nothing on buildings whose
//      lines said "Primary_Cleaner", and INSERTED A NEW LINE PRICED AT THE
//      WHOLE BUILDING. It produced three phantom lines across two buildings in
//      three days — $23,512.84 twice at Houston Physicians Hospital.
//
// ⛔ THE LINE IS NEVER ASKED ABOUT. You are already on it. The only difference
// between naming a sub for the first time and swapping one is whether the box
// starts empty:
//
//   empty box  → save_service_line(), which already takes provider_id and
//                writes it straight through. No ceremony, no approval.
//   filled box → the genuine sub-change flow, which ends the outgoing
//                arrangement on a named night, re-points open incentive
//                payouts and tells Finance the money moved. That path is
//                correct and it stays.
//
// ⛔ ONE PICKER. SmartSelect is the searchable picker in this app and a second
// one is never built. It is narrowed with isSelectable, never by filtering the
// fetch — filtering the fetch is what blanked 81 saved lines.
// ============================================================================

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SmartSelect, isAssignableSub } from '../../../components/smart-select';
import { Button, useToast } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { isRunningOrBooked } from '../../../shared/facility-is-live';

export interface SubLine {
  assignment_id: string;
  facility_id?: string | null;
  provider_id: string | null;
  provider_name?: string | null;
  service_code: string | null;
  service_label: string | null;
  assignment_status: string | null;
}

export function SubOnTheLineField({
  a,
  canManage,
  otherLines,
  onSaveAssignment,
  onRefresh,
}: {
  a: SubLine;
  canManage: boolean;
  /** Every OTHER line on this building, whatever its status. */
  otherLines: SubLine[];
  onSaveAssignment: (
    assignmentId: string,
    field: string,
    value: string | number | boolean | null,
  ) => Promise<{ ok: boolean; error: string | null }>;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = a.provider_name ?? a.provider_id ?? null;
  const hasSub = Boolean(a.provider_id);

  // ⛔ ALL TICKED BY DEFAULT, and the list grows on its own — do NOT branch on
  // the count. Measured 2026-09-15: 201 buildings have one line and 9 have two,
  // none has three or more, and NO BUILDING ANYWHERE HAS TWO DIFFERENT SUBS ON
  // IT. So one line asks nothing at all, and where there is a second the honest
  // default is that the same person does both.
  const alsoCandidates = useMemo(
    () => otherLines.filter((l) => isRunningOrBooked(l.assignment_status)),
    [otherLines],
  );
  const [alsoApply, setAlsoApply] = useState<Record<string, boolean>>({});
  const tickedFor = (id: string) => alsoApply[id] ?? true;

  function start() {
    setPicked(a.provider_id ?? null);
    setAlsoApply({});
    setError(null);
    setEditing(true);
  }

  async function save() {
    if (!picked) {
      setError('Choose a sub, or close this without changing anything.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const targets = [a.assignment_id, ...alsoCandidates.filter((l) => tickedFor(l.assignment_id)).map((l) => l.assignment_id)];
      for (const id of targets) {
        const res = await onSaveAssignment(id, 'provider_id', picked);
        // ⛔ A refused field is not a thrown error here — the save door answers
        // per field — so a failure has to be read, not assumed away.
        if (!res.ok) throw new Error(res.error ?? 'The save was refused.');
      }
      toast.success(
        targets.length === 1
          ? 'Sub recorded on this line.'
          : `Sub recorded on ${targets.length} lines.`,
      );
      setEditing(false);
      onRefresh();
    } catch (e) {
      setError(errorMessage(e, 'Could not record the sub.', 'sub-on-the-line'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="md:col-span-2">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        Sub on this line
      </dt>
      {!editing && (
        <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm text-tcs-text">
          <span>{current ?? 'Nobody recorded'}</span>
          {canManage && (
            <button
              type="button"
              onClick={
                hasSub
                  ? () =>
                      navigate(
                        `/sub-replacement/new?facility_id=${encodeURIComponent(
                          a.facility_id ?? '',
                        )}&assignment_id=${encodeURIComponent(a.assignment_id)}`,
                      )
                  : start
              }
              className="rounded border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs text-tcs-text hover:bg-tcs-surface"
            >
              {hasSub ? 'Change' : 'Record the sub'}
            </button>
          )}
        </dd>
      )}
      {editing && (
        <dd className="mt-1 space-y-2">
          <SmartSelect
            entity="service_provider"
            value={picked}
            onChange={(id) => setPicked(id || null)}
            entityLabel="sub"
            placeholder="Search subs…"
            disabled={saving}
            isSelectable={isAssignableSub}
          />
          {alsoCandidates.length > 0 && (
            <div className="rounded-md border border-tcs-border bg-tcs-raised p-2">
              <p className="text-[11px] text-tcs-text-secondary">
                Also on this building — untick any line this sub does not do:
              </p>
              <ul className="mt-1 space-y-1">
                {alsoCandidates.map((l) => (
                  <li key={l.assignment_id} className="flex items-center gap-2 text-xs text-tcs-text">
                    <input
                      id={`also__${l.assignment_id}`}
                      type="checkbox"
                      checked={tickedFor(l.assignment_id)}
                      disabled={saving}
                      onChange={(e) =>
                        setAlsoApply((prev) => ({ ...prev, [l.assignment_id]: e.target.checked }))
                      }
                    />
                    <label htmlFor={`also__${l.assignment_id}`}>
                      {l.service_label ?? 'Service line'}
                      {l.provider_name ? ` — currently ${l.provider_name}` : ' — nobody recorded'}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && <p className="text-xs text-rag-red">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </dd>
      )}
    </div>
  );
}
